import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import styled, { keyframes } from "styled-components/macro";
import { LayoutList } from "./LayoutList";
import { OverlayImageContainer } from "../Image/OverlayImageContainer";
import { SurfaceLayoutGroup } from "../../api/generated/models";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import { ACTIVATE_SURFACE_LAYOUTS, SET_ACTIVE_LAYOUT_GROUP } from "../../store/actions/configurationActions";
import client from "../../api/client/SdsWebApiClient";
import {
    getAccount,
    getActiveLayoutGroup,
    getAppConfig,
    getComposedUrl,
    getImageAndMask,
    getLanguage,
    getSessionId,
} from "../../store/sagas/selectors";
import {
    buildSelectionHighlightData,
    extractMaskData,
    extractMaskMetadata,
    getMaskMetadataForMouseEvent,
    MaskMetadata,
} from "../../utilities/maskUtils";
import loadPngMetadata from "../../utilities/pngMetaLoader";
import Constants from "../../constants";
import { ConfigImageContainer } from "../Image/ConfigImageContainer";
import { ConfigImage } from "../Image/ConfigImage";
import { SelectionCanvas } from "../Image/SelectionCanvas";
import { SelectionHighlightData } from "../../models/selectionHightlightData";

const onLoadAnimation = keyframes`
    0% {bottom: 30px; opacity:0;}
    100% {bottom: 40px; opacity:1;}
`;

const onLoadAnimationMobile = keyframes`
    0% {bottom: -6px; opacity:0;}
    100% {bottom: 6px; opacity:1;}
`;

const Container = styled.div<ContainerProps>`
    display: ${props => (props.active ? "inline-block" : "none")};
`;

const ButtonContainer = styled.div<ButtonContainerProps>`
    width: 100%;
    pointer-events: none;
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: absolute;
    z-index: 5;
    animation-name: ${props => (props.isMobile ? onLoadAnimationMobile : onLoadAnimation)};
    animation-duration: 0.5s;
    animation-timing-function: ease-in-out;
    bottom: ${props => (props.isMobile ? "6px" : "40px")};
    ${({ theme }) => theme.bp.xxxl`
    transform: scale(1.4, 1.4);
        transform-origin: bottom center;
    `}
`;

const Button = styled.button<ButtonProps>`
    font-family: ${props => props.theme.fontFamily};
    font-size: 16px;
    pointer-events: auto;
    color: ${props => (props.primary ? "#fff" : "#000")};
    padding: 10px 20px;
    background: ${props => (props.primary === "primary" ? "var(--primary-color)" : "var(--light-active-color)")};
    margin: 10px;
    border-radius: 20px;
    border: none;
    box-shadow: 0px 3px 10px 0 rgb(0 0 0 / 20%);
    transition: background 0.3s ease-in-out;
    @media (hover) {
        &:hover {
            background: ${props =>
                props.primary === "primary" ? "var(--primary-hover-color)" : "var(--light-hover-color)"};
            cursor: pointer;
        }
    }
    &:disabled {
        background: #b9b9b9;
        cursor: context-menu;
        color: #626262;
    }
`;

const MaskImage = styled.img`
    position: fixed;
    flex: 1 1 auto;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -3;
`;

interface ButtonProps {
    primary?: string;
}

interface ContainerProps {
    active: Boolean;
}

interface ButtonContainerProps {
    isMobile: Boolean;
}

export const LayoutOverlay = ({
    layoutGroups,
    isActive,
    onClose,
    onLoad,
}: {
    layoutGroups: SurfaceLayoutGroup[];
    isActive: Boolean;
    onClose: Function;
    onLoad: Function;
}) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(getSessionId);
    const initialImageAndMask = useSelector(getImageAndMask);
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const account = useSelector(getAccount);
    const composedUrl = useSelector(getComposedUrl);
    const activeLayoutGroup = useSelector(getActiveLayoutGroup);
    const [previewLayoutGroups, setPreviewLayoutGroups] = useState<SurfaceLayoutGroup[]>(layoutGroups);
    const initialImage = initialImageAndMask?.configurationImagePath || undefined;
    const initialMask = initialImageAndMask?.maskPath || undefined;
    const [imageUrl, setImageUrl] = useState<string | undefined>(initialImage);
    const [maskUrl, setMaskUrl] = useState<string | undefined>(initialMask);
    const [maskData, setMaskData] = useState<any>();
    const [maskMetadata, setMaskMetadata] = useState<MaskMetadata>();
    const [highlightData, setHighlightData] = useState<SelectionHighlightData>();
    const [isItemSelectable, setIsItemSelectable] = useState(false);
    const [loading, setLoading] = useState(false);
    const configImageRef = useRef<HTMLImageElement>(null);
    const maskRef = useRef<HTMLImageElement>(null);

    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;

    const handleMouseMove = (event: any) => {
        const metadata = getMaskMetadataForMouseEvent(event, maskRef.current, maskData, maskMetadata);
        setIsItemSelectable(!!metadata?.[Constants.layoutGroupMetadataKey]);
    };

    const setActiveImage = async (layoutGroup: string, layout: string) => {
        const currentLayouts = previewLayoutGroups.map(l =>
            l.id === layoutGroup ? { ...l, activeLayout: layout } : l
        );
        setPreviewLayoutGroups(currentLayouts);
        triggerLayoutGroupBlink(layoutGroup);
        const changedLayouts: { [key: string]: string } = {};
        currentLayouts.forEach(lg => {
            changedLayouts[lg.id] = lg.activeLayout;
        });
        const { configString } = await client.previewSurfaceLayoutsPost(sessionId, changedLayouts);
        const { imagePath, maskPath } = await client.getConfigImageAndMaskGet(
            configString,
            undefined,
            undefined,
            undefined,
            window.innerHeight,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            "jpg",
            account?.id,
            false
        );
        setImageUrl(composedUrl + imagePath);
        setMaskUrl(composedUrl + maskPath);
    };

    const loadLayoutConfigurations = () => {
        setLoading(true);
        const changedLayouts: { [key: string]: string } = {};
        previewLayoutGroups?.forEach(lg => {
            changedLayouts[lg.id] = lg.activeLayout;
        });

        const changed = layoutConfigurationChanged();
        // Update all the layouts.
        dispatch({ type: ACTIVATE_SURFACE_LAYOUTS, changedLayouts });
        onLoad(changed);
    };

    const handleMaskLoaded = async () => {
        if (!maskRef.current || !maskUrl) return;
        const rawMetadata = await loadPngMetadata(maskUrl);
        const data = extractMaskData(maskRef.current);
        const metadataByValue = extractMaskMetadata(rawMetadata);
        setMaskData(data);
        setMaskMetadata(metadataByValue);
    };

    const triggerLayoutGroupBlink = (layoutGroup: string) => {
        if (!maskMetadata || activeLayoutGroup === layoutGroup) return;
        const selectionValues = Object.entries(maskMetadata)
            .filter(([, attributes]) => attributes[Constants.layoutGroupMetadataKey] === layoutGroup)
            .map(([colorValue]) => parseInt(colorValue, 10));

        const newHighlightData = buildSelectionHighlightData(
            maskData,
            maskRef.current,
            configImageRef.current,
            selectionValues
        );

        setHighlightData(newHighlightData);
    };

    const onImageClick = (event: any) => {
        const metadata = getMaskMetadataForMouseEvent(event, maskRef.current, maskData, maskMetadata);
        const layoutGroup = metadata?.[Constants.layoutGroupMetadataKey];
        if (!layoutGroup) return;
        triggerLayoutGroupBlink(layoutGroup);
        dispatch({ type: SET_ACTIVE_LAYOUT_GROUP, activeLayoutGroup: layoutGroup });
    };

    const layoutConfigurationChanged = () => {
        let changed = false;

        layoutGroups?.forEach(lg => {
            const layout = previewLayoutGroups?.find(f => f.id === lg.id);
            if (!changed && layout?.activeLayout !== lg.activeLayout) changed = true;
        });

        return changed;
    };

    const imageWidth = configImageRef.current?.naturalWidth;
    const imageHeight = configImageRef.current?.naturalHeight;
    return (
        <Container active={isActive}>
            <OverlayImageContainer>
                <ConfigImageContainer imageWidth={imageWidth} imageHeight={imageHeight}>
                    <ConfigImage
                        ref={configImageRef}
                        src={imageUrl || ""}
                        alt=""
                        onClick={onImageClick}
                        onMouseMove={handleMouseMove}
                        isSelectableItem={isItemSelectable}
                    />
                    <MaskImage
                        onLoad={handleMaskLoaded}
                        crossOrigin="anonymous"
                        ref={maskRef}
                        alt=""
                        src={maskUrl || ""}
                    />
                    <SelectionCanvas highlightData={highlightData} />
                </ConfigImageContainer>
            </OverlayImageContainer>
            <LayoutList
                layoutGroups={previewLayoutGroups}
                onImageClick={setActiveImage}
                onChangeLayoutGroup={triggerLayoutGroupBlink}
            />

            <ButtonContainer isMobile={isMobile}>
                <Button
                    primary="primary"
                    disabled={loading}
                    onClick={() => {
                        loadLayoutConfigurations();
                    }}
                >
                    {!loading
                        ? get(appConfig, `translations.${language}.loadLayouts`, "loadLayouts")
                        : get(appConfig, `translations.${language}.loading`, "Loading ...")}
                </Button>
                <Button
                    onClick={() => {
                        onClose();
                    }}
                >
                    {get(appConfig, `translations.${language}.closeLayouts`, "closeLayouts")}
                </Button>
            </ButtonContainer>
        </Container>
    );
};
